import { useEffect, useState, type ReactNode } from "react";
import { effectiveSize } from "../lib/state/selectors";
import type { DoorObject } from "../lib/state/visualizerStore";
import type { DoorSize } from "../lib/geometry/doorSize";
import type { TopProfile } from "../lib/api/types";

// Soft-fill door glyphs. Everything is drawn with `currentColor`, so the icon inherits the tile's
// text colour (brand-active when selected, muted otherwise) and the subtle body fill tints with it.
const glyphBody = { fill: "currentColor", fillOpacity: 0.16, stroke: "currentColor", strokeWidth: 1.5, strokeLinejoin: "round" as const };
const glyphRib = { stroke: "currentColor", strokeWidth: 1 };

function SizeGlyph({ size, w = 28 }: { size: DoorSize; w?: number }) {
  // Single ≈ near-square (8×7); double is the same height, twice as wide (16×7) — the width contrast
  // is the whole signal.
  return size === "single" ? (
    <svg viewBox="0 0 24 24" width={w} height={w} aria-hidden="true">
      <rect x="6" y="7" width="12" height="10.5" rx="1.5" {...glyphBody} />
      <line x1="6" y1="10.5" x2="18" y2="10.5" {...glyphRib} />
      <line x1="6" y1="14" x2="18" y2="14" {...glyphRib} />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width={w} height={w} aria-hidden="true">
      <rect x="1.5" y="7" width="21" height="10.5" rx="1.5" {...glyphBody} />
      <line x1="1.5" y1="10.5" x2="22.5" y2="10.5" {...glyphRib} />
      <line x1="1.5" y1="14" x2="22.5" y2="14" {...glyphRib} />
    </svg>
  );
}

function ShapeGlyph({ kind, w = 28 }: { kind: TopProfile["kind"]; w?: number }) {
  if (kind === "chamfer")
    return (
      <svg viewBox="0 0 24 24" width={w} height={w} aria-hidden="true">
        <path d="M4 21 L4 9.5 L8 5 L16 5 L20 9.5 L20 21 Z" {...glyphBody} />
        <line x1="4" y1="13" x2="20" y2="13" {...glyphRib} />
        <line x1="4" y1="17" x2="20" y2="17" {...glyphRib} />
      </svg>
    );
  if (kind === "arc")
    return (
      <svg viewBox="0 0 24 24" width={w} height={w} aria-hidden="true">
        <path d="M4 21 L4 11 A8 7 0 0 1 20 11 L20 21 Z" {...glyphBody} />
        <line x1="4" y1="14.5" x2="20" y2="14.5" {...glyphRib} />
        <line x1="4" y1="17.8" x2="20" y2="17.8" {...glyphRib} />
      </svg>
    );
  return (
    <svg viewBox="0 0 24 24" width={w} height={w} aria-hidden="true">
      <rect x="4" y="6" width="16" height="15" rx="1.5" {...glyphBody} />
      <line x1="4" y1="11" x2="20" y2="11" {...glyphRib} />
      <line x1="4" y1="16" x2="20" y2="16" {...glyphRib} />
    </svg>
  );
}

// A selectable icon + caption tile. Mirrors SegButton's selected treatment so the geometry controls
// read as peers of the mode toggle above the preview.
function GlyphTile({ active, label, title, onClick, children }: {
  active: boolean;
  label: string;
  title: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-pressed={active}
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        padding: "6px 8px 5px",
        minWidth: 56,
        cursor: "pointer",
        borderRadius: "var(--radius-sm)",
        border: `1px solid ${active ? "var(--brand)" : "var(--border-strong)"}`,
        background: active ? "var(--brand-tint)" : "var(--surface)",
        color: active ? "var(--brand-active)" : "var(--muted)",
        transition: "border-color 120ms ease, background 120ms ease, color 120ms ease",
      }}
    >
      {children}
      <span style={{ fontSize: 11, fontWeight: 600 }}>{label}</span>
    </button>
  );
}

const shapeLabel: Record<TopProfile["kind"], string> = { flat: "Flat", chamfer: "Clipped", arc: "Arch" };

// Size + top shape for one opening on a single wrapping row: the size pair, a gap, then the shape trio.
// Two aria-labelled groups give the split meaning without a visible "Size"/"Top shape" label — the
// icon captions already say what each is. Wraps to two lines in the narrowest column.
function OpeningTiles({ size, shape, onSize, onShape }: {
  size: DoorSize;
  shape: TopProfile["kind"];
  onSize: (s: DoorSize) => void;
  onShape: (k: TopProfile["kind"]) => void;
}) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
      <div role="group" aria-label="Door size" style={{ display: "flex", gap: 6 }}>
        <GlyphTile active={size === "single"} label="Single" title="Single 8×7" onClick={() => onSize("single")}><SizeGlyph size="single" /></GlyphTile>
        <GlyphTile active={size === "double"} label="Double" title="Double 16×7" onClick={() => onSize("double")}><SizeGlyph size="double" /></GlyphTile>
      </div>
      <div role="group" aria-label="Top shape" style={{ display: "flex", gap: 6 }}>
        <GlyphTile active={shape === "flat"} label="Flat" title="Flat top" onClick={() => onShape("flat")}><ShapeGlyph kind="flat" /></GlyphTile>
        <GlyphTile active={shape === "chamfer"} label="Clipped" title="Clipped corners" onClick={() => onShape("chamfer")}><ShapeGlyph kind="chamfer" /></GlyphTile>
        <GlyphTile active={shape === "arc"} label="Arch" title="Arched top" onClick={() => onShape("arc")}><ShapeGlyph kind="arc" /></GlyphTile>
      </div>
    </div>
  );
}

const sizeOf = (d: DoorObject): DoorSize => effectiveSize(d);
const kindOf = (d: DoorObject): TopProfile["kind"] => d.topProfile?.kind ?? "flat";
const doorSummary = (d: DoorObject) => `${sizeOf(d) === "single" ? "Single" : "Double"} · ${shapeLabel[kindOf(d)]}`;

// One door in its own bounding box: a header (number pill + name + live summary) over the icon row.
function DoorBox({ door, onSize, onShape }: {
  door: DoorObject;
  onSize: (id: string, size: DoorSize) => void;
  onShape: (id: string, kind: TopProfile["kind"]) => void;
}) {
  return (
    <div style={{ border: "1px solid var(--border-strong)", borderRadius: "var(--radius-sm)", padding: "10px 11px", background: "var(--surface)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
        <span style={{ flexShrink: 0, minWidth: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 6px", borderRadius: "var(--radius-pill)", fontSize: 11, fontWeight: 700, background: "var(--brand-tint)", color: "var(--brand-active)" }}>
          {door.index + 1}
        </span>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Door {door.index + 1}</span>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--muted)" }}>{doorSummary(door)}</span>
      </div>
      <OpeningTiles size={sizeOf(door)} shape={kindOf(door)} onSize={(s) => onSize(door.id, s)} onShape={(k) => onShape(door.id, k)} />
    </div>
  );
}

// Size + top shape are properties of each OPENING, not of a catalogue choice, so they show whenever a
// door is being edited. Each door in scope is its own standalone bounding box — ALL-doors mode boxes
// every door (a single+double pair is common, so sizes/shapes are always independently editable);
// individual mode boxes just the selected door. Styles still gang across doors — that's stated once
// by the caller, above the picker.
// startCollapsed (mobile 1-door sessions): the panel opens as a one-line summary row showing the
// live detected verdict with an explicit "Adjust" affordance — a wrong detection stays visible and
// one tap from correction, while the common correct case stops paying ~120px of permanent controls.
export function DoorGeometryPanel({ doors, allMode, selected, startCollapsed = false, onSize, onShape }: {
  doors: DoorObject[];
  allMode: boolean;
  selected: DoorObject | null;
  startCollapsed?: boolean;
  onSize: (id: string, size: DoorSize) => void;
  onShape: (id: string, kind: TopProfile["kind"]) => void;
}) {
  const [expanded, setExpanded] = useState(!startCollapsed);
  // Adding a door re-opens the panel regardless of selection mode: the new door lands as an
  // unfitted quad that needs size/shape attention (keyed on doors.length, not the edit scope —
  // handleAddDoor selects the new door individually, so the scope stays length 1 after an add).
  useEffect(() => {
    if (doors.length > 1) setExpanded(true);
  }, [doors.length]);

  const targets = allMode ? doors : selected ? [selected] : [];
  if (targets.length === 0) return null;

  if (!expanded) {
    const d = targets[0];
    return (
      <div className="rg-geom-panel" style={{ display: "flex", flexDirection: "column" }}>
        <button
          type="button"
          data-testid="geometry-summary"
          onClick={() => setExpanded(true)}
          style={{
            display: "flex", alignItems: "center", gap: 8, width: "100%", minHeight: 44,
            padding: "10px 11px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
            background: "var(--surface)", cursor: "pointer", textAlign: "left", font: "inherit", color: "var(--text)",
          }}
        >
          <span style={{ flexShrink: 0, minWidth: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 6px", borderRadius: "var(--radius-pill)", fontSize: 11, fontWeight: 700, background: "var(--brand-tint)", color: "var(--brand-active)" }}>
            {d.index + 1}
          </span>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Door {d.index + 1}</span>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>{doorSummary(d)}</span>
          <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: "var(--brand-active)" }}>Adjust ›</span>
        </button>
      </div>
    );
  }

  return (
    <div className="rg-geom-panel" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {targets.map((d) => (
        <DoorBox key={d.id} door={d} onSize={onSize} onShape={onShape} />
      ))}
    </div>
  );
}
